import { type z } from 'zod';

import { api } from '@/api/client';
import { type AdapterFn } from '@/lib/adapters/httpAdapter.types';
import { resolveUrl } from '@/lib/formatters';
import { normalizeError } from '@/lib/transformers/normalizeError';
import { parseResponse } from '@/lib/validators/parseResponse';

type HttpMethod = 'post' | 'put' | 'delete';

export type HttpMutationAdapterOptions<TResponse, TParams> = {
  url: string | ((params: TParams) => string);
  /**
   * Validates the response payload. Omit it for endpoints that return no content —
   * the adapter then resolves to `undefined` without inspecting the response.
   */
  schema?: z.ZodType<TResponse>;
  mapBody?: (params: TParams) => unknown;
  mapParams?: (params: TParams) => Record<string, unknown>;
  /**
   * Set to false when the schema validates the full envelope (e.g. `{ data, pageable }`)
   * instead of just the `data` payload. Defaults to true.
   */
  unwrap?: boolean;
};

export function createHttpMutationAdapter(method: HttpMethod) {
  return function httpMutationAdapter<TResponse = void, TParams = void>(
    options: HttpMutationAdapterOptions<TResponse, TParams>
  ): AdapterFn<TParams, TResponse> {
    const { url, schema, mapBody, mapParams, unwrap = true } = options;

    return (async (params?: TParams) => {
      try {
        const urlString = resolveUrl(url, params);
        const body = mapBody && params !== undefined ? mapBody(params) : undefined;
        const queryParams = mapParams && params !== undefined ? mapParams(params) : undefined;

        // Axios only accepts a DELETE body through the `data` config field.
        const res =
          method === 'delete'
            ? await api.delete(urlString, { params: queryParams, data: body })
            : await api[method](urlString, body, { params: queryParams });

        if (!schema) {
          return undefined as TResponse;
        }

        return parseResponse(res, schema, unwrap);
      } catch (error) {
        return normalizeError(error);
      }
    }) as AdapterFn<TParams, TResponse>;
  };
}
